import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import Dialog from 'components/dialog';
import TextField from 'components/text-field';
import { Copy } from 'lucide-react';
import Mui from 'materials';
import React, { PropsWithChildren, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import titleCase from 'utils/title-case';
import { useCreateCalibration } from 'views/bluviu-nutrition/features/calibrations/hooks/use-create-calibration';
import { CalibrationModel } from 'views/bluviu-nutrition/features/calibrations/types/calibration-model.enum';
import { CloneCalibrationRequest } from 'views/bluviu-nutrition/features/calibrations/types/clone-calibration-request.type';
import { getCalibrationType } from 'views/bluviu-nutrition/features/calibrations/utils/get-calibration-type';
import { useSelectedSite } from 'views/bluviu-nutrition/hooks/use-selected-site';
import { useCalibrationListState } from '../context/calibration-list-context';

type Props = PropsWithChildren & {
  button?: React.ReactElement;
  calibration: {
    name: string;
    id: string;
    model: CalibrationModel;
  };
};

const CloneCalibrationDialog: React.FC<Props> = ({ calibration, button }) => {
  const history = useHistory();
  const intl = useIntl();
  const { cloneCalibration } = useCreateCalibration();
  const { selectedSiteId, selectedSite } = useSelectedSite();
  const {
    calibrationsQuery: { data: calibrations },
  } = useCalibrationListState();

  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const invalidNames = calibrations?.map(({ name }) => name) ?? [];

  const { control, reset, handleSubmit, formState } = useForm<CloneCalibrationRequest>({
    defaultValues: {
      name: '',
      calibrationId: calibration.id,
    },
    mode: 'onChange',
  });

  const onSubmit = (data: CloneCalibrationRequest) => {
    cloneCalibration.mutate(
      { model: calibration.model, request: { ...data, calibrationId: calibration.id } }, // setting calibration id in default values is forgotten if a calibration row is removed from the list calibration component.
      {
        onSuccess: (clonedCalibration) => {
          reset();
          dialogRef.current?.setOpen(false);
          history.push(
            `/bluviu-nutrition/calibrations/${selectedSiteId}/${getCalibrationType(clonedCalibration.model)}/${clonedCalibration.id}`
          );
        },
      }
    );
  };

  return (
    <Dialog
      ref={dialogRef}
      maxWidth={'xs'}
      title={`Clone calibration (${calibration.name})`}
      button={
        button || (
          <Tooltip title={'Clone'}>
            <Mui.IconButton>
              <Copy size={22} />
            </Mui.IconButton>
          </Tooltip>
        )
      }>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography fontSize={14}>From site: {selectedSite?.name}</Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <TextField
              dark
              fullWidth
              controller={{
                control,
                name: 'name',
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: (value) =>
                    !invalidNames.includes(value)
                      ? true
                      : `Name must not match an existing calibration within the selected site.`,
                },
              }}
              label={'Calibration name'}
            />
            <TextField dark disabled fullWidth value={titleCase(calibration.model)} label={'Calibration model'} />
            <Box display={'flex'} justifyContent={'end'}>
              <Button
                type="submit"
                disabled={!formState.isValid || cloneCalibration.isPending}
                startIcon={cloneCalibration.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
                Clone
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};

export default React.memo(CloneCalibrationDialog);
